import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useAskAssistantMutation, useListAssistantMessagesQuery } from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  ASSISTANT_MAX_QUESTION,
  assistantErrorText,
  assistantQuestionLost,
  type AssistantMessage,
  type AssistantSource,
} from '@entities/assistant/types';
import { AGENT_LABELS } from '@shared/agents/labels';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
`;

const Thread = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Bubble = styled.div<{ $role: 'user' | 'assistant'; $pending?: boolean }>`
  max-width: 88%;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  border: 1px solid ${({ theme }) => theme.colors.border};
  ${({ $role, theme }) =>
    $role === 'user'
      ? css`
          align-self: flex-end;
          background: ${theme.colors.primarySoft};
        `
      : css`
          align-self: flex-start;
          background: ${theme.colors.surface};
        `}
  ${({ $pending, theme }) =>
    $pending &&
    css`
      color: ${theme.colors.text.muted};
      font-style: italic;
    `}
`;

const Sources = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(2)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: normal;
`;

const SourceLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const Welcome = styled.div`
  margin: auto 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  line-height: 1.5;

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: 16px;
  }
`;

const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Suggestion = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const ErrorLine = styled.div`
  margin: 0 ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.status.danger};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

const Composer = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: flex-end;
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)} 0;
`;

const Field = styled.textarea`
  flex: 1;
  min-width: 0;
  min-height: 44px;
  max-height: 160px;
  resize: vertical;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 14px;
  line-height: 1.45;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Hint = styled.div`
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)} ${theme.spacing(3)}`};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Подсказки для пустого чата: про текущий раздел и общие вопросы. */
function suggestionsFor(page: string | undefined): string[] {
  const code = page?.match(/^\/agents\/([a-z]+)/)?.[1];
  const label = code ? AGENT_LABELS[code] : undefined;
  const out: string[] = [];
  if (label) out.push(`Что можно делать в разделе ${label}?`);
  out.push('Как настроить двухфакторный вход?');
  out.push('Как получить доступ к другому разделу?');
  out.push('Что нового в ОСНОВЕ?');
  return out;
}

/**
 * «По [d1] — …»: метка файла Диска в тексте ответа — тоже ссылка на файл (у
 * источника заголовок «[d1] Инструкция….txt (Общие › …)»). Метки без файла
 * остаются текстом.
 */
function withDiskCites(content: string, files: readonly AssistantSource[]): ReactNode[] {
  const byCite = new Map<string, AssistantSource>();
  for (const f of files) {
    const m = /^\[(d\d+)\]/.exec(f.title);
    if (m && f.url) byCite.set(m[1], f);
  }
  if (byCite.size === 0) return [content];
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of content.matchAll(/\[(d\d+)\]/g)) {
    const f = byCite.get(m[1]);
    if (!f?.url || m.index === undefined) continue;
    out.push(content.slice(last, m.index));
    out.push(
      <SourceLink
        key={m.index}
        to={f.url}
        title={f.title}
        aria-label={`Файл на Диске: ${f.title.replace(/^\[d\d+\]\s*/, '')}`}
      >
        {m[0]}
      </SourceLink>,
    );
    last = m.index + m[0].length;
  }
  out.push(content.slice(last));
  return out;
}

/** Нормы и ЛНА — подписью, файлы с Диска — ссылками на файл. */
function sourcesOf(m: AssistantMessage): { norms: string | null; files: AssistantSource[] } {
  if (m.role !== 'assistant' || !Array.isArray(m.sources)) return { norms: null, files: [] };
  const files = m.sources.filter((s) => s.type === 'disk' && s.url);
  const norms = m.sources.filter((s) => !files.includes(s)).map((s) => s.title);
  return { norms: norms.length > 0 ? norms.join('; ') : null, files };
}

/**
 * Чат с Помощником: лента своего чата, ввод вопроса, ожидание ответа.
 * Ответ приходит целиком (до минуты и дольше) — показываем индикатор.
 * `sessionId = null` — новый чат: сервер создаст его по первому вопросу,
 * id вернётся через `onSessionChange`. `page` — адрес страницы, откуда
 * спрашивают: Помощник учитывает раздел.
 */
export function AssistantChat({
  sessionId,
  onSessionChange,
  page,
}: {
  sessionId: string | null;
  onSessionChange: (id: string) => void;
  page?: string;
}) {
  const [text, setText] = useState('');
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLTextAreaElement>(null);

  const { data, isFetching } = useListAssistantMessagesQuery(sessionId ?? '', {
    skip: !sessionId,
  });
  const messages = useMemo(() => (sessionId ? (data?.items ?? []) : []), [data, sessionId]);
  const [ask, { isLoading: asking }] = useAskAssistantMutation();

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, draft, asking]);

  // Смена чата — сбрасываем ошибку и ставим курсор в поле.
  useEffect(() => {
    setError(null);
    fieldRef.current?.focus();
  }, [sessionId]);

  async function send(question: string) {
    const q = question.trim();
    if (!q || asking) return;
    setError(null);
    setText('');
    setDraft(q);
    try {
      const res = await ask({ question: q, session_id: sessionId ?? undefined, page }).unwrap();
      if (res.session.id !== sessionId) onSessionChange(res.session.id);
    } catch (e) {
      setError(assistantErrorText(e));
      if (assistantQuestionLost(e)) setText(q);
    } finally {
      setDraft(null);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void send(text);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send(text);
    }
  }

  const empty = messages.length === 0 && !draft && !(isFetching && sessionId);

  return (
    <Wrap>
      <Thread ref={threadRef} aria-live="polite">
        {empty ? (
          <Welcome>
            <strong>Помощник ОСНОВЫ</strong>
            <span>
              Спросите, как что-то сделать в ОСНОВЕ, что значит термин или статус, или задайте
              рабочий вопрос — по строительству, сметам, документации, снабжению, кадрам, праву.
            </span>
            <Suggestions>
              {suggestionsFor(page).map((s) => (
                <Suggestion key={s} type="button" disabled={asking} onClick={() => void send(s)}>
                  {s}
                </Suggestion>
              ))}
            </Suggestions>
          </Welcome>
        ) : (
          <>
            {messages.map((m) => {
              const src = sourcesOf(m);
              return (
                <Bubble key={m.id} $role={m.role}>
                  {m.role === 'assistant' ? withDiskCites(m.content, src.files) : m.content}
                  {(src.norms || src.files.length > 0) && (
                    <Sources>
                      {src.norms && <div>Нормы: {src.norms}</div>}
                      {src.files.length > 0 && (
                        <div>
                          Файлы на Диске:{' '}
                          {src.files.map((f, i) => (
                            <span key={f.url}>
                              {i > 0 && '; '}
                              <SourceLink to={f.url ?? '/disk'}>{f.title}</SourceLink>
                            </span>
                          ))}
                        </div>
                      )}
                    </Sources>
                  )}
                </Bubble>
              );
            })}
            {draft && <Bubble $role="user">{draft}</Bubble>}
            {asking && (
              <Bubble $role="assistant" $pending>
                Помощник думает… обычно это занимает до минуты.
              </Bubble>
            )}
          </>
        )}
      </Thread>
      {error && <ErrorLine role="alert">{error}</ErrorLine>}
      <Composer onSubmit={onSubmit}>
        <Field
          ref={fieldRef}
          rows={2}
          value={text}
          maxLength={ASSISTANT_MAX_QUESTION}
          placeholder="Ваш вопрос… Enter — отправить, Shift+Enter — новая строка"
          aria-label="Вопрос Помощнику"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <Button type="submit" disabled={asking || !text.trim()}>
          {asking ? 'Ждём…' : 'Отправить'}
        </Button>
      </Composer>
      <Hint>
        Отвечает модель DeepSeek — не вставляйте персональные данные. Помощник может ошибаться:
        важное перепроверяйте.
      </Hint>
    </Wrap>
  );
}
